import { useQuery } from '@tanstack/react-query'
import { AlertTriangle, CheckCircle2, Database, RefreshCw, XCircle } from 'lucide-react'
import clsx from 'clsx'
import { dbConsoleService, formatBytes } from '@/services/dbConsole'
import { count } from './format'
import { archives, lifecycleSummary } from './lifecycle'

function when(iso: string | null): string {
  if (!iso) return '—'
  const parsed = new Date(iso.endsWith('Z') || iso.includes('+') ? iso : `${iso}Z`)
  return Number.isNaN(parsed.getTime()) ? iso : parsed.toLocaleString()
}

/** Every figure here is measured from the live engine on each load. A metric
 *  the engine cannot answer renders as "not reported by this engine" with the
 *  reason — never as a plausible-looking number. */
export function HealthPanel() {
  const query = useQuery({
    queryKey: ['db-console', 'health'],
    queryFn: dbConsoleService.health,
    refetchInterval: 30_000,
  })

  const data = query.data

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto">
      <section className="flex flex-wrap items-center gap-3 rounded-lg border border-amber-300/10 bg-[#0b0b0b] px-3 py-2.5">
        <span
          className={clsx(
            'flex items-center gap-1.5 rounded px-2 py-1 text-[11px] font-medium',
            !data
              ? 'bg-white/5 text-stone-400'
              : data.healthy
                ? 'bg-emerald-500/10 text-emerald-300'
                : 'bg-red-500/10 text-red-300',
          )}
        >
          {!data ? (
            <Database size={12} />
          ) : data.healthy ? (
            <CheckCircle2 size={12} />
          ) : (
            <AlertTriangle size={12} />
          )}
          {!data ? 'Checking…' : data.healthy ? 'All checks passing' : 'Attention required'}
        </span>

        <dl className="flex flex-wrap items-center gap-x-5 gap-y-1 text-[11px]">
          {[
            ['Engine', data?.engine.version ?? data?.engine.dialect ?? '—'],
            ['Size on disk', data ? formatBytes(data.engine.sizeBytes) : '—'],
            ['Tables', data ? `${data.totals.tablesPresent} / ${data.totals.tables}` : '—'],
            ['Rows stored', data ? count(data.totals.rows) : '—'],
            ['Probe latency', data?.engine.latencyMs != null ? `${data.engine.latencyMs} ms` : '—'],
            [
              'Active connections',
              data?.engine.activeConnections != null ? count(data.engine.activeConnections) : null,
            ],
          ].map(([label, value]) => (
            <div key={String(label)}>
              <dt className="text-[9px] uppercase tracking-[0.12em] text-stone-600">{label}</dt>
              <dd className={clsx('font-mono', value === null ? 'text-stone-600' : 'text-stone-100')}>
                {value === null ? 'not reported by this engine' : value}
              </dd>
            </div>
          ))}
        </dl>

        <button
          type="button"
          onClick={() => query.refetch()}
          disabled={query.isFetching}
          className="ml-auto flex items-center gap-1 rounded border border-amber-300/15 px-2 py-1 text-[10px] text-stone-300 transition hover:border-amber-300/30 hover:text-amber-100 disabled:opacity-40"
        >
          <RefreshCw size={10} className={clsx(query.isFetching && 'animate-spin')} /> Re-check
        </button>
      </section>

      {data?.engine.sizeNote && (
        <p className="rounded border border-amber-300/10 bg-black/30 px-3 py-1.5 text-[10px] text-stone-500">
          {data.engine.sizeNote}
        </p>
      )}

      <section className="grid gap-2 md:grid-cols-3">
        {(data?.checks ?? []).map((check) => (
          <div
            key={check.id}
            className={clsx(
              'rounded-lg border px-3 py-2',
              check.ok ? 'border-emerald-500/20 bg-emerald-500/[0.04]' : 'border-red-500/25 bg-red-500/[0.05]',
            )}
          >
            <div className="flex items-center gap-1.5 text-[11px] font-medium text-stone-100">
              {check.ok ? (
                <CheckCircle2 size={12} className="text-emerald-300" />
              ) : (
                <XCircle size={12} className="text-red-300" />
              )}
              {check.label}
            </div>
            <p className="mt-0.5 text-[10px] leading-relaxed text-stone-400">{check.detail}</p>
          </div>
        ))}
      </section>

      <section className="min-h-0 overflow-auto rounded-lg border border-amber-300/10 bg-[#0c0c0c]">
        <table className="w-full border-collapse text-left">
          <thead className="sticky top-0 bg-[#121212]">
            <tr className="text-[9px] uppercase tracking-[0.12em] text-stone-500">
              <th scope="col" className="px-2.5 py-2 font-medium">
                Table
              </th>
              <th scope="col" className="px-2.5 py-2 text-right font-medium">
                Rows
              </th>
              <th scope="col" className="px-2.5 py-2 text-right font-medium">
                Columns
              </th>
              <th scope="col" className="px-2.5 py-2 text-right font-medium">
                Indexes
              </th>
              <th scope="col" className="px-2.5 py-2 font-medium">
                Last write
              </th>
              <th scope="col" className="px-2.5 py-2 font-medium">
                Status
              </th>
            </tr>
          </thead>
          <tbody>
            {(data?.tables ?? []).map((table) => (
              <tr key={table.table} className="border-b border-white/[0.04] text-[11px]">
                <td className="px-2.5 py-1.5">
                  <div className="text-stone-200">{table.label}</div>
                  <div className="font-mono text-[9px] text-stone-600">{table.table}</div>
                </td>
                <td className="px-2.5 py-1.5 text-right font-mono text-stone-100">
                  {table.rows === null ? '—' : count(table.rows)}
                  {/* A soft-deleting table's stored rows, split: the raw count
                      alone read 407 deals against a pipeline of 161. */}
                  {archives(table.lifecycle) && (
                    <div className="text-[9px] text-stone-500">{lifecycleSummary(table.lifecycle)}</div>
                  )}
                </td>
                <td className="px-2.5 py-1.5 text-right font-mono text-stone-400">
                  {table.columns}
                </td>
                <td className="px-2.5 py-1.5 text-right font-mono text-stone-400">
                  {table.indexes}
                </td>
                <td className="px-2.5 py-1.5 font-mono text-[10px] text-stone-400">
                  {when(table.lastWriteAt)}
                </td>
                <td className="px-2.5 py-1.5">
                  {!table.present ? (
                    <span className="rounded bg-red-500/10 px-1.5 py-0.5 text-[9px] uppercase tracking-wider text-red-300">
                      Missing from database
                    </span>
                  ) : !table.rowAccess ? (
                    <span className="rounded bg-amber-500/10 px-1.5 py-0.5 text-[9px] uppercase tracking-wider text-amber-300">
                      Contents sealed
                    </span>
                  ) : (
                    <span className="rounded bg-emerald-500/10 px-1.5 py-0.5 text-[9px] uppercase tracking-wider text-emerald-300">
                      OK
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {data && data.largestTables.length > 0 && (
        <section className="rounded-lg border border-amber-300/10 bg-[#0b0b0b] px-3 py-2">
          <div className="mb-1 text-[9px] uppercase tracking-[0.16em] text-stone-600">
            Largest tables on disk
          </div>
          <ul className="grid gap-x-4 gap-y-0.5 text-[11px] md:grid-cols-2">
            {data.largestTables.map((entry) => (
              <li key={entry.table} className="flex justify-between gap-3">
                <span className="truncate font-mono text-stone-400">{entry.table}</span>
                <span className="font-mono text-stone-200">
                  {formatBytes(entry.bytes)}
                  <span className="ml-2 text-stone-600">~{count(entry.approxRows)} rows</span>
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {data && (
        <p className="px-1 pb-1 text-[9px] text-stone-600">
          Measured {when(data.checkedAt)} directly against the database this deployment is connected
          to. Values the engine does not expose are shown as not reported rather than estimated.
        </p>
      )}
    </div>
  )
}
